import React, { useEffect, useState } from 'react';
import { LeaveApplication, LeaveApplicationResponse, LeaveStatus } from '../../interfaces/leave.interface';
import { LeaveService } from '../../services/leave.service';
import { LeaveTypeService } from '../../services/leave-type.service';
import { EmployeeLetterService } from '../../services/employee-letter.service';
import { IMaternityBenefitStatement } from '../../interfaces/maternityBenefitStatement.interface';
import { IMaternityBenefitInstalment } from '../../interfaces/maternityBenefitStatement.interface';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Chip,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Stack,
    TablePagination,
    Link,
    Tooltip,
    Divider,
    CircularProgress
} from '@mui/material';
import { TextField, MenuItem, IconButton } from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import { format } from 'date-fns';
import { useToast } from '../../context/ToastContext';
import ResponsiveTable from '../common/ResponsiveTable';
import { Column } from '../common/responsiveTable.types';
import { formatMoney } from '../../utils/format';
import { cleanLeaveReason, displayPersonName, displayUsername } from '../../utils/userUtils';
import { EmployeeAutocomplete } from '../common/EmployeeAutocomplete';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';

interface InstitutionUser {
    id: string;
    username: string;
    phone: string;
    type: string;
    isActive: boolean;
}

type LeaveRow = LeaveApplication;

interface ConfirmDialogProps {
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    onClose: () => void;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({ open, title, message, onConfirm, onClose }) => (
    <Dialog open={open} onClose={onClose} PaperProps={{ 'data-help-page': 'leave.confirmForm.page' } as any} maxWidth="sm" fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent data-help-section="leave.confirmForm.section.message">
            <Typography>{message}</Typography>
        </DialogContent>
        <DialogActions>
            <Button data-help="leave.confirmForm.cancel" onClick={onClose}>Cancel</Button>
            <Button data-help="leave.confirmForm.confirm" onClick={onConfirm} variant="contained" color="primary" autoFocus>
                Confirm
            </Button>
        </DialogActions>
    </Dialog>
);

const AllLeaveApplications: React.FC = () => {
    const [leaveData, setLeaveData] = useState<LeaveApplicationResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [selectedLeaveId, setSelectedLeaveId] = useState<string | null>(null);
    const toast = useToast();
    const [approveDialogOpen, setApproveDialogOpen] = useState(false);
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    // Board B-08: leave ids the server refused to reject (payroll month finalized) -> the reason, shown as the button tooltip.
    const [rejectBlocked, setRejectBlocked] = useState<Record<string, string>>({});
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [selectedUser, setSelectedUser] = useState<InstitutionUser | null>(null);
    const canViewPayroll = usePermission(PERMISSIONS.PAYROLL_VIEW);
    const canManagePayroll = usePermission(PERMISSIONS.PAYROLL_MANAGE);
    const canApproveLeave = usePermission(PERMISSIONS.LEAVE_APPLICATION_APPROVE);
    // Maternity-shaped leave types (docs/decisions/2026-09-08-maternity-pay-basis.md P1/P3):
    // isPaid AND a non-null minimumTenureMonths -- the same marker the backend uses to 404
    // everything else. Loaded once so the "Benefit statement" action only appears where the
    // backend would actually have one, rather than showing on every row and letting the click
    // discover the 404.
    const [maternityTypeNames, setMaternityTypeNames] = useState<Set<string>>(new Set());
    const [statementLoadingId, setStatementLoadingId] = useState<string | null>(null);
    const [statement, setStatement] = useState<IMaternityBenefitStatement | null>(null);
    const [statementDialogOpen, setStatementDialogOpen] = useState(false);
    // §47 instalments (task-3-brief.md, 2026-09-11 payroll-money close-out lane): kept as its own
    // list, refreshed independently of `statement` after a create/delete so the read-only §47/§48
    // schedule above never needs to be recomputed just to reflect a live instalment change.
    const [instalments, setInstalments] = useState<IMaternityBenefitInstalment[]>([]);
    const [creatingInstalment, setCreatingInstalment] = useState(false);
    const [deletingInstalmentId, setDeletingInstalmentId] = useState<string | null>(null);
    const [instalmentToDelete, setInstalmentToDelete] = useState<string | null>(null);
    const [newInstalmentTranche, setNewInstalmentTranche] = useState<'PRE_DELIVERY' | 'POST_DELIVERY' | ''>('');
    const [newInstalmentYear, setNewInstalmentYear] = useState('');
    const [newInstalmentMonth, setNewInstalmentMonth] = useState('');

    useEffect(() => {
        fetchAllLeaveApplications();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage, selectedUser?.id]);

    useEffect(() => {
        if (!canViewPayroll) return;
        LeaveTypeService.getByInstitution()
            .then((types) => {
                const names = types
                    .filter((t) => t.isPaid === true && t.minimumTenureMonths != null)
                    .map((t) => t.name);
                setMaternityTypeNames(new Set(names));
            })
            .catch(() => setMaternityTypeNames(new Set()));
    }, [canViewPayroll]);

    // §47 instalments (task-3-brief.md, 2026-09-11 payroll-money close-out lane, fix round 1):
    // appended effects rather than folded into the existing open/close handlers, so
    // handleViewBenefitStatement and the dialog's onClose/Close-button stay byte-identical to
    // develop. Seed instalments whenever `statement` changes (i.e. right after
    // handleViewBenefitStatement's existing setStatement(data) call); reset the new state when
    // the dialog closes, without ever touching `statement` itself.
    useEffect(() => {
        setInstalments(statement?.instalments ?? []);
    }, [statement]);

    useEffect(() => {
        if (!statementDialogOpen) {
            setInstalments([]);
            setNewInstalmentTranche('');
            setNewInstalmentYear('');
            setNewInstalmentMonth('');
        }
    }, [statementDialogOpen]);

    const fetchAllLeaveApplications = async () => {
        try {
            const data = await LeaveService.getAllApplicationsByInstitution(page, rowsPerPage, selectedUser?.id);
            setLeaveData(data);
        } catch (err) {
            toast.showApiError(err, 'Failed to fetch leave applications');
        } finally {
            setLoading(false);
        }
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleApproveClick = (id: string) => {
        setSelectedLeaveId(id);
        setApproveDialogOpen(true);
    };

    const handleViewBenefitStatement = async (id: string) => {
        setStatementLoadingId(id);
        try {
            const data = await LeaveService.getMaternityBenefitStatement(id);
            setStatement(data);
            setStatementDialogOpen(true);
        } catch (err) {
            toast.showApiError(err, 'Failed to load the maternity benefit statement');
        } finally {
            setStatementLoadingId(null);
        }
    };

    const [statementPdfLoading, setStatementPdfLoading] = useState(false);
    const handleDownloadStatementPdf = async () => {
        if (!statement) return;
        setStatementPdfLoading(true);
        try {
            await EmployeeLetterService.downloadMaternityStatement(statement.leaveApplicationId, statement.employeeCode);
        } catch (err) {
            toast.showApiError(err, 'Failed to download the maternity benefit statement');
        } finally {
            setStatementPdfLoading(false);
        }
    };

    const refreshInstalments = async (leaveApplicationId: string) => {
        try {
            const data = await LeaveService.getMaternityInstalments(leaveApplicationId);
            setInstalments(data);
        } catch (err) {
            toast.showApiError(err, 'Failed to reload the instalments');
        }
    };

    const handleCreateInstalment = async () => {
        if (!statement || !newInstalmentTranche || !newInstalmentYear || !newInstalmentMonth) return;
        setCreatingInstalment(true);
        try {
            await LeaveService.createMaternityInstalment(statement.leaveApplicationId, {
                installment: newInstalmentTranche,
                targetYear: Number(newInstalmentYear),
                targetMonth: Number(newInstalmentMonth)
            });
            setNewInstalmentTranche('');
            setNewInstalmentYear('');
            setNewInstalmentMonth('');
            await refreshInstalments(statement.leaveApplicationId);
        } catch (err) {
            toast.showApiError(err, 'Failed to create the instalment');
        } finally {
            setCreatingInstalment(false);
        }
    };

    const handleDeleteInstalment = async (instalmentId: string) => {
        if (!statement) return;
        setDeletingInstalmentId(instalmentId);
        try {
            await LeaveService.deleteMaternityInstalment(statement.leaveApplicationId, instalmentId);
            await refreshInstalments(statement.leaveApplicationId);
        } catch (err) {
            toast.showApiError(err, 'Failed to delete the instalment');
        } finally {
            setDeletingInstalmentId(null);
        }
    };

    const handleRejectClick = (id: string) => {
        setSelectedLeaveId(id);
        setRejectDialogOpen(true);
    };

    const handleApproveConfirm = async () => {
        if (!selectedLeaveId) return;
        try {
            await LeaveService.approveLeaveApplication(selectedLeaveId);
            await fetchAllLeaveApplications();
            setApproveDialogOpen(false);
            toast.showSuccess('Leave application approved');
        } catch (err) {
            toast.showApiError(err, 'Failed to approve leave application');
        }
    };

    const handleRejectConfirm = async () => {
        if (!selectedLeaveId) return;
        try {
            await LeaveService.rejectLeaveApplication(selectedLeaveId);
            await fetchAllLeaveApplications();
            setRejectDialogOpen(false);
            toast.showSuccess('Leave application rejected');
        } catch (err) {
            const reason = (err as any)?.response?.status === 409 ? (err as any)?.response?.data : null;
            if (typeof reason === 'string' && reason) {
                setRejectBlocked(prev => ({ ...prev, [selectedLeaveId]: reason }));
                setRejectDialogOpen(false);
            }
            toast.showApiError(err, 'Failed to reject leave application');
        }
    };

    const getStatusColor = (status: LeaveStatus) => {
        switch (status) {
            case LeaveStatus.Approved:
                return 'success';
            case LeaveStatus.Rejected:
                return 'error';
            case LeaveStatus.Cancelled:
                return 'warning';
            default:
                return 'default';
        }
    };

    const formatDateTime = (dateString: string) => {
        return format(new Date(dateString), 'dd-MM-yyyy');
    };

    const columns: Column<LeaveRow>[] = [
        { key: 'employee', helpKey: 'leave.all.col.employee', header: 'Employee', priority: 1, render: leave => {
            const name = displayPersonName(leave.userDisplayName, leave.userName);
            return <Typography data-help="leave.all.col.employee" variant="body2">{name}</Typography>;
        } },
        { key: 'leaveType', header: 'Leave Type', priority: 1, helpKey: 'leave.all.col.leaveType', render: leave => leave.leaveType },
        {
            key: 'reason', helpKey: 'leave.all.col.reason', header: 'Reason', render: leave => (
                <Tooltip title={cleanLeaveReason(leave.leaveReason)} placement="top-start" arrow>
                    <Box
                        data-help="leave.all.col.reason"
                        sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            wordBreak: 'break-word',
                            maxWidth: 260,
                        }}
                    >
                        {cleanLeaveReason(leave.leaveReason)}
                    </Box>
                </Tooltip>
            ),
        },
        { key: 'applied', header: 'Application Date', render: leave => <Box data-help="leave.all.col.applied" sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(leave.applicationDate)}</Box> },
        { key: 'start', header: 'Start Date', render: leave => <Box data-help="leave.all.col.start" sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(leave.startDate)}</Box> },
        { key: 'end', header: 'End Date', render: leave => <Box data-help="leave.all.col.end" sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(leave.endDate)}</Box> },
        { key: 'days', header: 'Days', helpKey: 'leave.all.col.days', render: leave => leave.numberOfDays },
        {
            key: 'status', helpKey: 'leave.all.col.status', header: 'Status', priority: 1, render: leave => (
                <Chip
                    data-help="leave.all.col.status"
                    label={leave.leaveStatus}
                    color={getStatusColor(leave.leaveStatus) as any}
                    size="small"
                    sx={{ pointerEvents: 'none' }}
                />
            ),
        },
        {
            key: 'approver', helpKey: 'leave.all.col.approver', header: 'Approver', render: leave => leave.actionedByName ? (
                <Box data-help="leave.all.col.approver">
                    <Typography variant="body2">{leave.actionedByDisplayName || displayUsername(leave.actionedByName)}</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {leave.actionedAt ? format(new Date(leave.actionedAt), 'dd MMM yyyy, hh:mm a') : ''}
                    </Typography>
                </Box>
            ) : (
                <Typography data-help="leave.all.col.approver" variant="body2" color="text.secondary">—</Typography>
            ),
        },
        {
            key: 'documents', helpKey: 'leave.all.col.documents', header: 'Documents', render: leave => leave.documents && leave.documents.length > 0 ? (
                <Box data-help="leave.all.col.documents">
                    {leave.documents.map((doc) => (
                        <Link
                            key={doc.id}
                            component="button"
                            variant="body2"
                            onClick={() => LeaveService.downloadDocument(doc.id, doc.originalFileName)}
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}
                        >
                            <AttachFileIcon fontSize="small" />
                            {doc.originalFileName}
                        </Link>
                    ))}
                </Box>
            ) : (
                <Typography data-help="leave.all.col.documents" variant="body2" color="text.secondary">-</Typography>
            ),
        },
        {
            key: 'actions', header: 'Actions', helpKey: 'leave.all.col.actions', role: 'actions', render: leave => (
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                    {canApproveLeave && (
                        <>
                            <Button data-help="leave.approve"
                                variant="contained"
                                color="success"
                                size="small"
                                onClick={() => handleApproveClick(leave.id)}
                                disabled={leave.leaveStatus !== LeaveStatus.Pending}
                            >
                                Approve
                            </Button>
                            {/* LeaveApplicationService.reject accepts Pending AND Approved (a supervisor may withdraw an approval). */}
                            <Tooltip title={rejectBlocked[leave.id] ?? ''}>
                                <span>
                                    <Button data-help="leave.reject"
                                        variant="contained"
                                        color="error"
                                        size="small"
                                        onClick={() => handleRejectClick(leave.id)}
                                        disabled={!!rejectBlocked[leave.id] || (leave.leaveStatus !== LeaveStatus.Pending && leave.leaveStatus !== LeaveStatus.Approved)}
                                    >
                                        Reject
                                    </Button>
                                </span>
                            </Tooltip>
                        </>
                    )}
                    {canViewPayroll
                        && leave.leaveStatus === LeaveStatus.Approved
                        && maternityTypeNames.has(leave.leaveType) && (
                        <Button data-help="leave.all.action.maternity"
                            variant="outlined"
                            size="small"
                            onClick={() => handleViewBenefitStatement(leave.id)}
                            disabled={statementLoadingId === leave.id}
                        >
                            {statementLoadingId === leave.id
                                ? <CircularProgress size={16} />
                                : 'Maternity benefit'}
                        </Button>
                    )}
                </Stack>
            ),
        },
    ];

    if (loading) return <Typography>Loading...</Typography>;
    if (!leaveData) return <Typography>No leave applications found</Typography>;

    return (
        <Box data-help-section="leave.allApplications" sx={{ padding: 3 }}>
            <Typography variant="h5" sx={{ marginBottom: 3 }}>
                All Leave Applications
            </Typography>
            <EmployeeAutocomplete<InstitutionUser>
                data-help="leave.all.employeeFilter"
                label="Filter by employee"
                value={selectedUser}
                onChange={(newValue) => {
                    setSelectedUser(newValue);
                    setPage(0);
                }}
                onError={(err) => toast.showApiError(err, 'Failed to search employees')}
                sx={{ mb: 2, maxWidth: 400 }}
            />
            <Paper data-help-section="leave.all.table">
                <ResponsiveTable<LeaveRow>
                    pageKey="leave.all"
                    columns={columns}
                    rows={leaveData.content}
                    getRowKey={r => r.id}
                    emptyMessage="No leave applications found"
                />
                <TablePagination
                    data-help="leave.all.paging"
                    component="div"
                    count={leaveData.totalElements}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 25]}
                />
            </Paper>

            <ConfirmDialog
                open={approveDialogOpen}
                title="Approve Leave Application"
                message="Are you sure you want to approve this leave application?"
                onConfirm={handleApproveConfirm}
                onClose={() => setApproveDialogOpen(false)}
            />

            <ConfirmDialog
                open={rejectDialogOpen}
                title="Reject Leave Application"
                message="Are you sure you want to reject this leave application?"
                onConfirm={handleRejectConfirm}
                onClose={() => setRejectDialogOpen(false)}
            />

            <ConfirmDialog
                open={instalmentToDelete !== null}
                title="Delete instalment"
                message="Are you sure you want to delete this instalment? It will no longer be paid with payroll."
                onConfirm={async () => {
                    const id = instalmentToDelete;
                    setInstalmentToDelete(null);
                    if (id) await handleDeleteInstalment(id);
                }}
                onClose={() => setInstalmentToDelete(null)}
            />

            <Dialog
                open={statementDialogOpen}
                PaperProps={{ 'data-help-page': 'leave.maternity.statement.page' } as any}
                onClose={() => setStatementDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Maternity benefit statement</DialogTitle>
                <DialogContent dividers>
                    {statement && (
                        <Stack spacing={2}>
                            <Typography variant="body2" color="text.secondary">
                                {statement.employeeName ?? '—'}
                                {statement.employeeCode ? ` (${statement.employeeCode})` : ''}
                                {' — '}
                                {statement.leaveTypeName}, {formatDateTime(statement.startDate)} to{' '}
                                {formatDateTime(statement.endDate)} ({statement.totalLeaveDays} day(s))
                            </Typography>

                            <Typography variant="subtitle2">Wage basis</Typography>
                            <Typography variant="body2">
                                Window: {formatDateTime(statement.wageBasis.windowStart)} to{' '}
                                {formatDateTime(statement.wageBasis.windowEnd)}
                            </Typography>
                            {statement.wageBasis.computable ? (
                                <Typography variant="body2">
                                    Daily average wage: {formatMoney(statement.wageBasis.dailyAverageWage, 'BDT')}{' '}
                                    (total wages {formatMoney(statement.wageBasis.totalWages, 'BDT')} ÷{' '}
                                    {statement.wageBasis.daysWorked} day(s) worked)
                                </Typography>
                            ) : (
                                <Typography variant="body2" color="error">
                                    The daily wage cannot be worked out from recorded wages.{' '}
                                    {statement.wageBasis.estimatedDayRateFromSalaryStructure != null
                                        ? `Estimated daily rate from the salary structure: ${formatMoney(statement.wageBasis.estimatedDayRateFromSalaryStructure, 'BDT')} (basic salary ÷ 30)`
                                        : 'No estimate could be worked out from the salary structure either.'}
                                </Typography>
                            )}

                            <Divider />

                            <Typography variant="subtitle2">Payment schedule (two instalments)</Typography>
                            <TableContainer data-help-section="leave.maternity.schedule">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell data-help="leave.maternity.col.instalment">Instalment</TableCell>
                                            <TableCell data-help="leave.maternity.col.scheduleDays" align="right">Days</TableCell>
                                            <TableCell data-help="leave.maternity.col.amount" align="right">Amount</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {statement.installmentSchedule.map((tranche) => (
                                            <TableRow key={tranche.installment}>
                                                <TableCell>
                                                    {tranche.installment === 'PRE_DELIVERY' ? 'Pre-delivery' : 'Post-delivery'}
                                                </TableCell>
                                                <TableCell align="right">{tranche.days}</TableCell>
                                                <TableCell align="right">
                                                    {formatMoney(tranche.amount, 'BDT')}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        <TableRow>
                                            <TableCell><strong>Total</strong></TableCell>
                                            <TableCell align="right">
                                                <strong>{statement.totalLeaveDays}</strong>
                                            </TableCell>
                                            <TableCell align="right">
                                                <strong>
                                                    {formatMoney(statement.totalScheduledAmount, 'BDT')}
                                                </strong>
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                {statement.actualPayrollTreatment}
                            </Typography>

                            <Divider />

                            <Typography variant="subtitle2">Caveats</Typography>
                            {statement.caveats.map((caveat, idx) => (
                                <Typography key={idx} variant="caption" component="div" color="text.secondary">
                                    • {caveat}
                                </Typography>
                            ))}

                            <Typography variant="caption" color="text.secondary">
                                Generated at {format(new Date(statement.generatedAt), 'dd-MM-yyyy HH:mm')}
                            </Typography>

                            <Divider />

                            <Typography variant="subtitle2">Scheduled instalments</Typography>
                            <TableContainer data-help-section="leave.maternity.instalments">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell data-help="leave.maternity.col.tranche">Tranche</TableCell>
                                            <TableCell data-help="leave.maternity.col.span">Span</TableCell>
                                            <TableCell data-help="leave.maternity.col.days" align="right">Days</TableCell>
                                            <TableCell data-help="leave.maternity.col.dailyAverage" align="right">Daily average</TableCell>
                                            <TableCell data-help="leave.maternity.col.gross" align="right">Gross</TableCell>
                                            <TableCell data-help="leave.maternity.col.offset" align="right">Continuation offset</TableCell>
                                            <TableCell data-help="leave.maternity.col.net" align="right">Net</TableCell>
                                            <TableCell data-help="leave.maternity.col.targetMonth">Target month</TableCell>
                                            <TableCell data-help="leave.maternity.col.status">Status</TableCell>
                                            {canManagePayroll && <TableCell data-help="leave.maternity.col.actions" align="right">Actions</TableCell>}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {instalments.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={canManagePayroll ? 10 : 9}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        No instalments created yet.
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        {instalments.map((inst) => {
                                            const isApplied = inst.appliedAt != null;
                                            return (
                                                <TableRow key={inst.id}>
                                                    <TableCell>
                                                        {inst.installment === 'PRE_DELIVERY' ? 'Pre-delivery' : 'Post-delivery'}
                                                    </TableCell>
                                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                        {formatDateTime(inst.spanStart)} to {formatDateTime(inst.spanEnd)}
                                                    </TableCell>
                                                    <TableCell align="right">{inst.days}</TableCell>
                                                    <TableCell align="right">{formatMoney(inst.dailyAverageWage, 'BDT')}</TableCell>
                                                    <TableCell align="right">{formatMoney(inst.grossAmount, 'BDT')}</TableCell>
                                                    <TableCell align="right">{formatMoney(inst.continuationOffset, 'BDT')}</TableCell>
                                                    <TableCell align="right"><strong>{formatMoney(inst.netAmount, 'BDT')}</strong></TableCell>
                                                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                        {format(new Date(inst.targetYear, inst.targetMonth - 1, 1), 'MMM yyyy')}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={isApplied
                                                                ? `Applied ${inst.appliedAt ? formatDateTime(inst.appliedAt) : ''}`.trim()
                                                                : 'Pending'}
                                                            color={isApplied ? 'success' : 'default'}
                                                            size="small"
                                                            sx={{ pointerEvents: 'none' }}
                                                        />
                                                    </TableCell>
                                                    {canManagePayroll && (
                                                        <TableCell align="right">
                                                            {!isApplied && (
                                                                <IconButton data-help="leave.maternity.action.delete"
                                                                    size="small"
                                                                    aria-label={`Delete ${inst.installment} instalment`}
                                                                    onClick={() => setInstalmentToDelete(inst.id)}
                                                                    disabled={deletingInstalmentId === inst.id}
                                                                >
                                                                    <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                            )}
                                                        </TableCell>
                                                    )}
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {canManagePayroll && (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                    <TextField
                                        data-help="leave.maternity.newTranche"
                                        select
                                        label="Tranche"
                                        value={newInstalmentTranche}
                                        onChange={(e) =>
                                            setNewInstalmentTranche(e.target.value as 'PRE_DELIVERY' | 'POST_DELIVERY' | '')
                                        }
                                        sx={{ flex: '1 1 200px', maxWidth: 260 }}
                                    >
                                        {(['PRE_DELIVERY', 'POST_DELIVERY'] as const).map((tranche) => (
                                            <MenuItem
                                                key={tranche}
                                                value={tranche}
                                                disabled={instalments.some((i) => i.installment === tranche)}
                                            >
                                                {tranche === 'PRE_DELIVERY' ? 'Pre-delivery' : 'Post-delivery'}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    <TextField
                                        data-help="leave.maternity.newYear"
                                        label="Target year"
                                        type="number"
                                        value={newInstalmentYear}
                                        onChange={(e) => setNewInstalmentYear(e.target.value)}
                                        inputProps={{ min: 2020, max: 2100 }}
                                        sx={{ flex: '1 1 200px', maxWidth: 260 }}
                                    />
                                    <TextField
                                        data-help="leave.maternity.newMonth"
                                        label="Target month"
                                        type="number"
                                        value={newInstalmentMonth}
                                        onChange={(e) => setNewInstalmentMonth(e.target.value)}
                                        inputProps={{ min: 1, max: 12 }}
                                        sx={{ flex: '1 1 200px', maxWidth: 260 }}
                                    />
                                    <Button
                                        data-help="leave.maternity.action.create"
                                        variant="contained"
                                        onClick={handleCreateInstalment}
                                        disabled={
                                            creatingInstalment
                                            || !newInstalmentTranche
                                            || !newInstalmentYear
                                            || !newInstalmentMonth
                                        }
                                        sx={{ flex: '1 1 200px', maxWidth: 260 }}
                                    >
                                        {creatingInstalment ? <CircularProgress size={16} /> : 'Create instalment'}
                                    </Button>
                                </Box>
                            )}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button data-help="leave.maternity.action.pdf"
                        onClick={handleDownloadStatementPdf}
                        disabled={!statement || statementPdfLoading}
                        startIcon={statementPdfLoading ? <CircularProgress size={16} /> : undefined}
                    >
                        Download PDF
                    </Button>
                    <Button data-help="leave.maternity.statement.close" onClick={() => setStatementDialogOpen(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AllLeaveApplications;
